import { rgba } from 'polished';

const color = {
  // Tonal colours
  black: '#000000',
  grayDark: '#9B9B9B',
  grayMedium: '#C3C3C3',
  gray: '#E8E8E8',
  grayLight: '#EFEFEF',
  lightBeige: '#FFF9F4',
  beige: '#F6F6F6',
  darkBeige: '#F8F2EC',
  offWhite: '#FAFAFA',
  white: '#FFFFFF',
  // Brand colours
  purpleDark: '#621698',
  purple: '#7A1BBE',
  purpleLight: '#9549CB',
  greenDark: '#007D79',
  green: '#00827E',
  greenLight: '#4DC7C4',
  cyanDark: '#517788',
  cyan: '#6595AA',
  cyanLight: '#84AABB',
  mediumBlueDark: '#27334A',
  mediumBlue: '#31405C',
  mediumBlueLight: '#5A667D',
  // Functional colours
  navyDark: '#0D2C70',
  navy: '#10378C',
  navyLight: '#405FA3',
  blueDark: '#8590A3',
  blue: '#A6B4CC',
  blueLight: '#C1CBDB',
  yellowDark: '#F1C21B',
  mintDark: '#40B69B',
  mint: '#50E3C2',
  mintLight: '#73E9CE',
  orangeDark: '#CB5400',
  orange: '#FE6900',
  orangeLight: '#FE8733',
  redDark: '#A60014',
  red: '#CF0019',
  redLight: '#DD4D5E',
  pinkDark: '#C71E5B',
  pink: '#E00656',
  pinkLight: '#FA518E',
  // Offset colours - WIP
  offset: {
    purple: '#F7ECFB',
    greenLight: '#EBF9F9',
    green: '#70D2CF',
    orange: '#FFF3EB',
    red: '#FCEBED',
    blue: '#F8F9FB',
    navy: '#EFF0F2',
  },

  // Colors from web-app
  notification: '#F5A623',
  text: 'rgba(0,0,0,0.87)',
  alternateText: '#ffffff',
  shadow: 'rgba(0,0,0,1)',
  overlay: 'rgba(0,0,0,0.8)',
  overlayWhite: 'rgba(255,255,255,0.8)',
  postCellGradient: 'rgba(0,0,0,0), white',
  bgOrange: 'rgba(254, 105, 0, 0.08)',
  avatarBlue: '#0043CE',

  grey0: '#FAFAFA',
  // grey1: '#EFEFEF', => grayLight
  // grey2: '#EFEFEF', => grayLight
  grey3: '#E7E7E7',
  grey4: '#BABABA',
  // grey5: '#9B9B9B', => grayDark
  grey6: '#5A5A5A',
  grey7: '#8c8c8c',
  grey8: '#e8e8e8',
  grey9: '#C3C3C3',
  grey10: '#6D7278',
  grey11: '#666666',
  grey12: '#F9F9F9',
  grey13: 'rgba(0, 0, 0, 0.87)',
  grey14: 'rgba(0, 0, 0, 0.14)',
  grey15: 'rgba(0, 0, 0, 0.04)',
  primary: '#00827E',
  green0: '#007F80',
  green1: '#33BEC0',
  green2: '#006667',
  green3: '#E5F7F7',
  green6: '#00938F', // will merge color from th90 package

  purple0: '#6004a9',
  purple1: '#9610d7',
  purple2: 'rgba(105, 41, 196, 0.08)',
  purple3: '#6929C4',
  purple4: '#E8DAFF',

  yellow1: '#f2e8c2',
  yellow2: '#d9cfad',

  secondary: '#FF4081',
  materialError: '#F44336',
  error: '#DA1E28',

  blackAlpha: (alpha = 1) => `rgba(0, 0, 0, ${alpha})`,
  whiteAlpha: (alpha = 1) => `rgba(255, 255, 255, ${alpha})`,

  // From aulaui
  backgrounds: {
    primary: 'rgba(0, 175, 170, 0.08)',
    secondary: 'rgba(122, 27, 190, 0.08)',
    default: 'rgba(49, 64, 92, 0.08)',
    warning: 'rgba(254, 105, 0, 0.08)',
  },

  designSystem: {
    purple90: '#31135E',
    purple80: '#491D8B',
    purple70: '#6929C4',
    purple60: '#8A3FFC',
    purple50: '#A56EFF',
    purple30: '#D4BBFF',
    purple10: '#F6F2FF',

    grey90: '#212121',
    grey60: '#666666',
    grey50: '#898989',
    grey40: '#9E9E9E',
    grey30: '#C6C6C6',
    grey20: '#E0E0E0',
    grey10: '#FAFAFA',

    blackOpacity87: 'rgba(0, 0, 0, 0.87)',
    blackOpacity60: 'rgba(0, 0, 0, 0.6)',
    blackOpacity38: 'rgba(0, 0, 0, 0.38)',
    blackOpacity12: 'rgba(0, 0, 0, 0.12)',

    whiteOpacity74: 'rgba(255, 255, 255, 0.74)',

    error: '#DA1E28',
    notification: '#C74811',
    notificationLight: '#F1C21B',
    warning: '#B00020',
    success: '#198038',
    information: '#0043CE',
    marker: '#D12771',

    background: {
      purpleGradient: 'linear-gradient(180deg, #5A33CE 0%, #6929C4 100%)',
      grey: {
        hover: 'rgba(0, 0, 0, 0.04)',
        focus: 'rgba(0, 0, 0, 0.14)',
        pressed: 'rgba(0, 0, 0, 0.10)',
        selected: 'rgba(0, 0, 0, 0.08)',
      },
      lightPurple: {
        hover: 'rgba(98, 0, 238, 0.04)',
        focus: 'rgba(105, 41, 196, 0.17)',
        pressed: 'rgba(105, 41, 196, 0.1)',
        selected: 'rgba(105, 41, 196, 0.08)',
      },
      light: {
        error: '#FFF1F1',
        warning: '#FFF4CC',
        success: '#DEFBE6',
        information: '#EDF5FF',
      },

      purpleAlpha: (alpha = 1) => `rgba(212, 187, 255, ${alpha})`,
    },

    // Following colors are used for font color styling in aula-editor
    textColors: {
      purple: 'rgb(105, 41, 196)',
      blue: 'rgb(0, 67, 206)',
      green: 'rgb(25, 128, 56)',
      orange: 'rgb(199, 72, 17)',
      red: 'rgb(218, 30, 40)',
    },

    buttonStates: {
      primary: {
        hover: '#6f32c6',
        active: '#7b43cb',
        selected: '#783eca',
      },
      secondary: {
        hover: '#faf5ff',
        active: '#e5dbf5',
        selected: '#f0e9fb',
      },
    },
  },
};

const opacity = {
  alpha4: 0.04,
  alpha8: 0.08,
  alpha12: 0.12,
  alpha28: 0.28,
  alpha40: 0.4,
  alpha56: 0.56,
  alpha76: 0.76,
  alpha90: 0.9,

  alpha38: 0.38,
  alpha60: 0.6,
  alpha87: 0.87,
  alpha100: 0.1,
};

const spacing = {
  xxxSmall: '2px',
  xxSmall: '4px',
  xSmall: '8px',
  small: '12px',
  medium: '16px',
  large: '20px',
  xLarge: '24px',
  xxLarge: '32px',
  xxxLarge: '36px',
  xxxxLarge: '40px',
  giant: '44px',
  xGiant: '48px',
  xxGiant: '52px',
  xxxGiant: '56px',
  xxxxGiant: '60px',
};

export default {
  color,
  font: {
    family: {
      workSans: '"Work Sans", sans-serif',
      system:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
      mono:
        '"SF Mono", "Monaco", "Inconsolata", "Fira Mono", "Droid Sans Mono", "Source Code Pro", monospace',
    },
    weight: {
      regular: 400,
      medium: 500,
      semiBold: 600,
      bold: 700,
    },
    size: {
      small: '12px',
      copy: '14px',
      large: '16px',
      headline: '22px',
      display: '36px',
    },
    lineHeight: {
      small: '18px',
      copy: '20px',
      large: '24px',
      headline: '28px',
    },
  },
  // A choice we'll have to make soon enough, array or explicit naming?
  spacing,
  /*
  spacing: [
    0, 2, 4, 8, 12, 16, 20, 24, 32, 36, 40, 44, 48, 52, 56, 60, 64, 72
  ],
  */
  borderRadius: {
    small: '2px',
    medium: '4px',
    large: '8px',
    extraLarge: '28px',
  },
  opacity,
  transition: {
    speed: {
      xSlow: '750ms',
      slow: '450ms',
      default: '250ms',
      fast: '125ms',
      xFast: '50ms',
    },
    easing: {
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
    },
  },
  background: {
    transparent: {
      overlay: 'rgba(0,0,0,0.72)',
    },
    gradient: {
      purple: 'linear-gradient(67deg, #6004A9 0%, #9610D7 100%)',
    },
  },
  shadow: {
    xLow: '0 0 8px 0 rgba(0,0,0,0.08)',
    low: '0 0 8px 0 rgba(0,0,0,0.20)',
    default: '0 0 8px 0 rgba(0,0,0,0.30)',
    high: '0 2px 24px 0 rgba(0,0,0,0.16)',
    xHigh:
      '0px 6px 10px rgba(0, 0, 0, 0.14), 0px 1px 18px rgba(0, 0, 0, 0.12), 0px 3px 5px rgba(0, 0, 0, 0.2)',
  },
  order: {
    z0: 0,
    z1: 1,
    z2: 2,
    z3: 3,
    z4: 4,
    z5: 5,
    z999: 999,
    z9999: 9999,
    zMax: 2147483647,
    zInherit: 'inherit',
    zInitial: 'initial',
    zUnset: 'unset',
  },
  button: {
    primary: {
      color: color.white,
      border: '0',
      backgroundColor: color.green,
      hover: {
        backgroundColor: color.greenDark,
      },
      active: {
        backgroundColor: color.greenDark,
      },
      selected: {
        color: color.white,
        backgroundColor: color.greenDark,
      },
      disabled: {
        color: color.white,
        backgroundColor: color.green,
        border: '0',
      },
    },
    secondary: {
      color: color.designSystem.blackOpacity60,
      backgroundColor: color.white,
      border: '0',
      hover: {
        backgroundColor: color.grayLight,
      },
      active: {
        backgroundColor: color.beige,
      },
      selected: {
        color: color.designSystem.purple70,
        backgroundColor: color.purple2,
      },
      disabled: {
        color: color.designSystem.blackOpacity38,
        backgroundColor: color.designSystem.grey10,
        border: '0',
      },
    },
    tertiary: {
      color: color.green,
      border: '0',
      backgroundColor: color.beige,
      hover: {
        backgroundColor: color.grayLight,
      },
      active: {
        backgroundColor: color.beige,
      },
      selected: {
        color: color.green,
        backgroundColor: color.beige,
      },
      disabled: {
        color: color.green,
        border: '0',
        backgroundColor: color.beige,
      },
    },
    special: {
      color: color.green,
      border: `1px solid ${color.green}`,
      backgroundColor: color.white,
      hover: {
        backgroundColor: color.white,
      },
      active: {
        backgroundColor: color.white,
      },
      selected: {
        color: color.green,
        backgroundColor: color.white,
      },
      disabled: {
        color: color.green,
        border: `1px solid ${color.green}`,
        backgroundColor: color.white,
      },
    },
    transparent: {
      color: color.purple,
      border: '0',
      backgroundColor: rgba(color.purple, opacity.alpha8),
      hover: {
        backgroundColor: rgba(color.purple, opacity.alpha28),
      },
      active: {
        backgroundColor: rgba(color.purple, opacity.alpha8),
      },
      selected: {
        color: color.purple,
        backgroundColor: rgba(color.purple, opacity.alpha8),
      },
      disabled: {
        color: color.purple,
        border: '0',
        backgroundColor: rgba(color.purple, opacity.alpha8),
      },
    },
  },
  fab: {
    primary: {
      backgroundColor: color.purple3,
      width: '56px',
      height: '56px',
      color: color.white,
    },
    secondary: {
      backgroundColor: color.white,
      width: '40px',
      height: '40px',
      color: color.designSystem.blackOpacity60,
    },
  },
};

export const colors = color;
